import { useState } from 'preact/hooks';
import { studentNamesFromText } from '../../lib/student-import.ts';
import { studentDisplayName } from '../../lib/student-roster.ts';
import { useStore } from '../../store/store.ts';
import { closeModal, toast } from '../../ui/ui-store.ts';

const PHILO_OPTIONS = [
  'CPC',
  'Religion catholique',
  'Religion islamique',
  'Religion protestante',
  'Religion orthodoxe',
  'Religion israélite',
  'Morale',
] as const;

export function StudentModal({ studentId }: { studentId: string | null }) {
  const existing = useStore((s) =>
    studentId ? s.students.find((x) => x.id === studentId) : undefined,
  );

  // One name field: the split into prénom / nom was never filled in — class
  // lists arrive as a single line — and two empty boxes only made the form
  // longer. Existing `firstName` / `lastName` values are left untouched in the
  // store so older records and JSON backups keep round-tripping.
  const [name, setName] = useState(studentDisplayName(existing?.name ?? ''));
  const [birth, setBirth] = useState(existing?.birth ?? '');
  const [philo, setPhilo] = useState(existing?.philo ?? '');

  function save() {
    const cleaned = studentDisplayName(name.trim());
    if (!cleaned) {
      toast('Indique le nom de l’élève');
      return;
    }

    useStore.getState().saveStudent(studentId, {
      name: cleaned,
      firstName: existing?.firstName ?? '',
      lastName: existing?.lastName ?? '',
      birth,
      philo,
    });
    closeModal();
    toast('Élève enregistré');
  }

  function remove() {
    if (!studentId) return;
    if (!confirm(`Supprimer ${existing ? studentDisplayName(existing.name) : 'cet élève'} et ses données associées ?`)) return;

    useStore.getState().deleteStudent(studentId);
    closeModal();
    toast('Élève supprimé');
  }

  return (
    <>
      <div class="formGrid">
        <div style={{ gridColumn: '1/-1' }}>
          <label for="sname">Nom</label>
          <input
            id="sname"
            value={name}
            placeholder="Ex. Dupont Léa"
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <div class="small muted" style={{ marginTop: '4px' }}>
            Le numéro est attribué automatiquement, par ordre alphabétique.
          </div>
        </div>
        <div>
          <label for="sbirth">Date de naissance</label>
          <input
            id="sbirth"
            type="date"
            value={birth}
            onInput={(e) => setBirth(e.currentTarget.value)}
          />
        </div>
        <div>
          <label for="sphilo">Option philo</label>
          <select id="sphilo" value={philo} onChange={(e) => setPhilo(e.currentTarget.value)}>
            <option value="">Non précisée</option>
            {PHILO_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div class="row" style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer l’élève
        </button>
        {studentId && (
          <button class="btn danger" onClick={remove}>
            Supprimer
          </button>
        )}
      </div>
    </>
  );
}

export function BulkStudentsModal() {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');

  async function loadFile(file: File | undefined) {
    if (!file) return;
    setText(studentNamesFromText(await file.text()).join('\n'));
    setFileName(file.name);
  }

  function save() {
    const names = studentNamesFromText(text);

    const added = useStore.getState().bulkAddStudents(names);
    closeModal();
    toast(`${added} élève(s) ajouté(s)`);
  }

  return (
    <>
      <p class="muted">
        Choisis un fichier de ta tablette (CSV ou TXT), ou colle un élève par ligne.
      </p>
      <label class="filePicker">
        <span class="btn soft">📁 Choisir dans mes fichiers</span>
        <input
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          onChange={(event) => void loadFile(event.currentTarget.files?.[0])}
        />
      </label>
      {fileName && <div class="small importFileName">Fichier chargé : <b>{fileName}</b></div>}
      <textarea
        style={{ minHeight: '240px', marginTop: '12px' }}
        placeholder={'Léa Dupont\nTom Martin\nSofia Bernard'}
        value={text}
        onInput={(e) => setText(e.currentTarget.value)}
      />
      <div class="small muted" style={{ marginTop: '8px' }}>
        Les fichiers avec les colonnes « Nom » et « Prénom » sont reconnus automatiquement.
      </div>
      <div style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Importer la classe
        </button>
      </div>
    </>
  );
}
